import React from "react";
import { Link } from "react-router-dom";
import { getOptimizedImageUrl } from "../lib/cms";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#fbf9f5] text-[#2E4638] border-t border-[#E5DDCF] relative overflow-hidden">
      {/* Subtle organic top accent */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#1C4230] via-[#D9691E] to-[#1C4230]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand og Mission Column (5 cols) */}
          <div className="lg:col-span-5 space-y-5">
            <Link to="/" className="inline-flex items-center gap-3 focus:outline-none">
              <img
                src={getOptimizedImageUrl(
                  "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/caspers-logo.png",
                  600,
                  90
                )}
                alt="Caspers Træservice"
                className="site-logo"
              />
            </Link>

            <p className="text-sm text-[#3C5345] leading-relaxed max-w-md">
              Professionel og sikker træfældning og beskæring af alle typer træer.
              Uddannet fagmand med over 10 års erfaring i træfældning, topkapning og træpleje.
              Dækker hele Syddanmark med base i Fole ved Gram.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#152E22]">
                <i className="fa-solid fa-shield-check text-[#D9691E]" aria-hidden="true" />
                Forsikret op til 10 mio. kr. (Topdanmark)
              </span>
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#152E22]">
                <i className="fa-solid fa-award text-[#D9691E]" aria-hidden="true" />
                10+ års erfaring
              </span>
            </div>
          </div>

          {/* Navigation Links (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="font-bold text-xs uppercase tracking-wider text-[#1F4531]">
              Ydelser og Sider
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/"
                  className="text-[#2E4738] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all"
                >
                  Hjem
                </Link>
              </li>
              <li>
                <Link
                  to="/traefaeldning"
                  className="text-[#152E22] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all font-semibold"
                >
                  Træfældning
                </Link>
              </li>
              <li>
                <Link
                  to="/beskaering"
                  className="text-[#152E22] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all font-semibold"
                >
                  Beskæring
                </Link>
              </li>
              <li>
                <Link
                  to="/raadgivning"
                  className="text-[#2E4738] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all"
                >
                  Rådgivning
                </Link>
              </li>
              <li>
                <Link
                  to="/opsaetning-af-redekasser"
                  className="text-[#152E22] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all font-semibold"
                >
                  Opsætning af redekasser
                </Link>
              </li>
              <li>
                <Link
                  to="/kontakt"
                  className="text-[#2E4738] hover:text-[#D9691E] hover:translate-x-1 inline-block transition-all"
                >
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Details (4 cols) */}
          <div className="lg:col-span-4 space-y-4">
            <h4 className="font-bold text-xs uppercase tracking-wider text-[#1F4531]">
              Virksomhedsoplysninger
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0 text-[#D9691E] mt-0.5 text-base">
                  <i className="fa-solid fa-building" aria-hidden="true" />
                </div>
                <div>
                  <strong className="text-[#152E22] block font-bold">Caspers Træservice</strong>
                  <span className="text-[#3C5345]">Folevej 23</span>
                  <br />
                  <span className="text-[#3C5345]">6510 Gram</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0 text-[#D9691E] text-base">
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-xs text-[#62776C] block">Telefon:</span>
                  <a
                    href="tel:42169230"
                    className="text-[#152E22] font-bold hover:text-[#D9691E] transition-colors whitespace-nowrap inline-block"
                  >
                    42169230
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0 text-[#D9691E] text-base">
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-xs text-[#62776C] block">Email:</span>
                  <a
                    href="mailto:info@casperstraeservice.dk"
                    className="text-[#152E22] font-medium hover:text-[#D9691E] transition-colors whitespace-nowrap inline-block"
                  >
                    info@casperstraeservice.dk
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0 text-[#D9691E] text-base">
                  <i className="fa-solid fa-id-card" aria-hidden="true" />
                </div>
                <div>
                  <span className="text-xs text-[#62776C] block">CVR-nummer:</span>
                  <span className="text-[#152E22] font-mono font-medium whitespace-nowrap">
                    37232521
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright og attribution */}
        <div className="mt-12 pt-8 border-t border-[#E5DDCF] flex flex-col sm:flex-row items-center justify-between text-xs text-[#61776B] gap-4">
          <p>© {currentYear} Caspers Træservice. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/privatlivspolitik"
              className="text-[#3C5345] hover:text-[#D9691E] underline transition-colors"
            >
              Privatlivs- og persondatapolitik
            </Link>
            <span className="hidden sm:inline text-[#DDD4C5]">•</span>
            <span>Sikker og professionel træfældning i hele Syddanmark</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
